import { useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { toast } from 'sonner';
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern

// NEW_FUNCTION_JUSTIFIED: Dedicated hook for lead deletion state management
export function useLeadDeletion(clearSelection: () => void) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingLeadIds, setPendingLeadIds] = useState<Id<'leads'>[]>([]);

  const bulkDelete = useMutation(api.leads.deletion.bulkDeleteLeads);

  const requestDelete = useCallback((ids: Id<'leads'>[]) => {
    setPendingLeadIds(ids);
    setIsDialogOpen(true);
  }, []);

  const cancelDelete = useCallback(() => {
    setIsDialogOpen(false);
    setPendingLeadIds([]);
  }, []);

  const confirmDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      const result = await bulkDelete({ leadIds: pendingLeadIds });
      if (result.deletedCount > 0) {
        toast.success(`Deleted ${result.deletedCount} lead${result.deletedCount !== 1 ? 's' : ''}`);
      }
      if (result.failedCount > 0) {
        toast.error(
          `Failed to delete ${result.failedCount} lead${result.failedCount !== 1 ? 's' : ''}`
        );
      }
      clearSelection();
    } catch {
      toast.error('Failed to delete leads');
    } finally {
      setIsDeleting(false);
      setIsDialogOpen(false);
      setPendingLeadIds([]);
    }
  }, [bulkDelete, pendingLeadIds, clearSelection]);

  return {
    isDialogOpen,
    isDeleting,
    pendingCount: pendingLeadIds.length,
    requestDelete,
    confirmDelete,
    cancelDelete,
  };
}
